import { useState } from 'react';
import { Link } from '../../components/base/Link';
import { useTranslation } from 'react-i18next';
import { damayaUrl } from '../../seo/pricing';
import { asset } from '../../lib/assets';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

// Brand entry page for Damaya (打码鸭): the self-service product runs at damaya.cn; this page
// explains what it is and links there, so a search for the brand can land on the company site.
const STYLES = [
  ['damaya-style-before', 'styleBefore'],
  ['damaya-style-blur', 'styleBlur'],
  ['damaya-style-mosaic', 'styleMosaic'],
  ['damaya-style-solid', 'styleSolid'],
  ['damaya-style-eyebar', 'styleEyebar'],
] as const;

export default function Damaya() {
  const { t } = useTranslation();
  const features = ['f1', 'f2', 'f3', 'f4'] as const;
  const steps = ['s1', 's2', 's3'] as const;
  const faqs = ['q1', 'q2', 'q3', 'q4'] as const;
  const [active, setActive] = useState(0);
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <section className="relative overflow-hidden bg-brand-navy py-20">
        <div className="absolute inset-0">
          <img src={asset('/media/hero-street-poster.jpg')} alt="" aria-hidden="true" className="h-full w-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/70 via-brand-navy/85 to-brand-navy"></div>
        </div>
        <div className="relative z-10 mx-auto max-w-7xl px-6">
          <div className="mx-auto max-w-3xl text-center">
            <img src={asset('/images/brand/damaya-logo.webp')} alt="" aria-hidden="true" width="72" height="72" className="mx-auto mb-6 h-18 w-18 rounded-2xl bg-white p-2" />
            <h1 className="mb-4 text-4xl font-bold text-white md:text-5xl">{t('damayaPage.heroTitle')}</h1>
            <p className="text-xl leading-relaxed text-blue-100">{t('damayaPage.heroSubtitle')}</p>
            <p className="mt-4 text-sm text-blue-200/80">{t('damayaPage.heroNote')}</p>
            <div className="mt-8 flex flex-col justify-center gap-4 sm:flex-row">
              <a href={damayaUrl} className="whitespace-nowrap rounded-lg bg-cyan-500 px-8 py-4 text-center text-lg font-semibold text-slate-900 transition-colors hover:bg-cyan-400">{t('damayaPage.open')}</a>
              <a href={`${damayaUrl}/help`} className="whitespace-nowrap rounded-lg border border-white/30 bg-white/10 px-8 py-4 text-center text-lg font-semibold text-white transition-colors hover:bg-white/20">{t('damayaPage.help')}</a>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="mx-auto max-w-7xl px-6">
          <h2 className="text-center text-3xl font-bold text-gray-900">{t('damayaPage.featuresTitle')}</h2>
          <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            {features.map(key => (
              <article key={key} className="rounded-2xl border border-gray-200 bg-white p-6 shadow-card">
                <h3 className="text-lg font-semibold text-gray-900">{t(`damayaPage.${key}Title`)}</h3>
                <p className="mt-3 leading-relaxed text-gray-600">{t(`damayaPage.${key}Desc`)}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-gray-50 py-16">
        <div className="mx-auto max-w-7xl px-6">
          <h2 className="text-center text-3xl font-bold text-gray-900">{t('damayaPage.stylesTitle')}</h2>
          <p className="mx-auto mt-3 max-w-2xl text-center text-gray-600">{t('damayaPage.stylesDesc')}</p>
          <div className="mx-auto mt-10 max-w-5xl">
            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
              {STYLES.map(([file, key], i) => (
                <img
                  key={file}
                  src={asset(`/images/real/${file}.webp`)}
                  alt={t(`damayaPage.${key}`)}
                  width={1280}
                  height={722}
                  loading={i === 0 ? 'eager' : 'lazy'}
                  className={`w-full ${i === active ? '' : 'hidden'}`}
                />
              ))}
            </div>
            <p className="mt-3 text-center text-base font-medium text-gray-800">{t(`damayaPage.${STYLES[active][1]}`)}</p>
            <div className="mt-4 grid grid-cols-5 gap-2 sm:gap-3">
              {STYLES.map(([file, key], i) => (
                <button
                  key={file}
                  type="button"
                  onClick={() => setActive(i)}
                  aria-pressed={i === active}
                  className={`rounded-lg p-1 text-left transition ${i === active ? 'bg-brand-100 ring-2 ring-brand-600' : 'hover:bg-gray-200/70'}`}
                >
                  <img src={asset(`/images/real/${file}.webp`)} alt="" aria-hidden="true" loading="lazy" className="w-full rounded-md border border-gray-200" />
                  <span className={`mt-1 block truncate text-center text-xs sm:text-sm ${i === active ? 'font-medium text-gray-900' : 'text-gray-500'}`}>{t(`damayaPage.${key}`)}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="mx-auto max-w-5xl px-6">
          <h2 className="text-center text-3xl font-bold text-gray-900">{t('damayaPage.stepsTitle')}</h2>
          <ol className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
            {steps.map((key, index) => (
              <li key={key} className="rounded-2xl border border-gray-200 p-6">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-brand-600 font-bold text-white">{index + 1}</span>
                <h3 className="mt-4 text-lg font-semibold text-gray-900">{t(`damayaPage.${key}Title`)}</h3>
                <p className="mt-2 leading-relaxed text-gray-600">{t(`damayaPage.${key}Desc`)}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="bg-gray-50 py-16">
        <div className="mx-auto max-w-4xl px-6">
          <h2 className="text-center text-3xl font-bold text-gray-900">{t('damayaPage.faqTitle')}</h2>
          <dl className="mt-10 space-y-6">
            {faqs.map(key => (
              <div key={key} className="rounded-2xl bg-white p-6 shadow-card">
                <dt className="text-lg font-semibold text-gray-900">{t(`damayaPage.${key}`)}</dt>
                <dd className="mt-2 leading-relaxed text-gray-600">{t(`damayaPage.${key}A`)}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <section className="bg-brand-navy py-16">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <h2 className="text-3xl font-bold text-white">{t('damayaPage.companyTitle')}</h2>
          <p className="mt-4 leading-relaxed text-blue-100">{t('damayaPage.companyDesc')}</p>
          <div className="mt-8 flex flex-col justify-center gap-4 sm:flex-row">
            <a href={damayaUrl} className="whitespace-nowrap rounded-lg bg-cyan-500 px-8 py-3 font-semibold text-slate-900 hover:bg-cyan-400">{t('damayaPage.open')}</a>
            <Link to="/pricing" className="whitespace-nowrap rounded-lg border border-white/30 bg-white/10 px-8 py-3 font-semibold text-white hover:bg-white/20">{t('damayaPage.apiLink')}</Link>
            <Link to="/about" className="whitespace-nowrap rounded-lg border border-white/30 bg-white/10 px-8 py-3 font-semibold text-white hover:bg-white/20">{t('damayaPage.aboutLink')}</Link>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
